import { useState, useEffect, useRef } from 'react';
import './index.css';
import { PaperPlaneIcon, CalendarIcon, TagIcon, ShoppingCartIcon } from './Icons';

const CustomerTicketView = () => {
  const getHashFromURL = () => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('hash');
  };

  const [hash] = useState(getHashFromURL()); // Store hash in state

  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [accessToken, setAccessToken] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const fetchTicketData = async () => {
      if (!hash) { // Check if hash exists
        setError('No hash parameter provided in URL');
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        const response = await fetch(
          `/api/request_details_by_hash?hash=${hash}`,
          {
            method: "GET", // Explicitly declare GET
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch ticket data');
        }

        const data = await response.json();
        setTicketData(data);
        setAccessToken(data.access_token);
        setRequestId(data.request_id);
        
        // Transform communication feed into messages format
        // In your useEffect where you transform messages:
        const transformedMessages = data.communication_feed.map(item => ({
          id: Math.random().toString(36).substr(2, 9),
          sender: item.entry_type.toLowerCase().includes('agent') || 
                  item.entry_type.toLowerCase().includes('merchant') 
                    ? 'agent' 
                    : 'customer',
          text: item.entry,
          timestamp: new Date(item.timestamp).toLocaleString(),
          entry_type: item.entry_type // keep original entry_type if needed
        }));
        
        setMessages(transformedMessages);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTicketData();
  }, [hash]);

  const sendMessageToAPI = async (messageText) => {
    if (!accessToken) {
      throw new Error('Access token not available');
    }

    try {
      const response = await fetch('/api/send_msg_request', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          entry: messageText,
          entry_type: 'customer_message',
          request_id: requestId  // Using the requestId from props
        })
      });
  
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
  
      return await response.json();
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  };
  
  const handleSendMessage = async () => {
    if (message.trim()) {
      const newMessage = {
        id: Math.random().toString(36).substr(2, 9),
        sender: 'customer',
        text: message,
        timestamp: new Date().toLocaleString(),
      };
  
      // Optimistically update UI
      setMessages([...messages, newMessage]);
      setMessage('');
  
      try {
        await sendMessageToAPI(message);
        // If successful, the message is already in the UI
      } catch (error) {
        // Rollback if API call fails
        setMessages(messages.filter(msg => msg.id !== newMessage.id));
        setError('Failed to send message. Please try again.');
      }
    }
  };



  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md">
          <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
          <p className="text-gray-700">{error}</p>
          <p className="text-sm text-gray-500 mt-4">Please try again later or contact support.</p>
        </div>
      </div>
    );
  }

  if (!ticketData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-indigo-600 px-6 py-4">
            <h1 className="text-2xl font-bold text-white">Request #{ticketData.request_id}</h1>
            <p className="text-indigo-100">Status: {ticketData.status}</p>
          </div>

          {/* Main Content */}
          <div className="md:flex">
            {/* Ticket Details */}
            <div className="md:w-1/3 border-r border-gray-200 p-6 pb-3">
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Request Details</h2>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div className="ml-3">
                        <p className="text-sm text-gray-500">Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(ticketData.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <TagIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div className="ml-3">
                        <p className="text-sm text-gray-500">Type</p>
                        <p className="text-sm font-medium text-gray-900">{ticketData.request_type}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <ShoppingCartIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div className="ml-3">
                        <p className="text-sm text-gray-500">Order Number</p>
                        <p className="text-sm font-medium text-gray-900">{ticketData.order_number}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-2">Description</h3>
                  <p className="text-sm text-gray-700">{ticketData.request_description}</p>
                </div>
                
                {ticketData.transcript_summary && (
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-2">Summary</h3>
                  <p className="text-sm text-gray-700">{ticketData.transcript_summary}</p>
                </div>
              )}
                <hr />

              </div>
            </div>
            
            {/* Conversation */}
            <div className="md:w-2/3 p-6 pt-3">
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Request Timeline</h2>
                  
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {messages.map((msg) => (
                      <div key={msg.id} className={`flex ${msg.sender === 'customer' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-xs md:max-w-md rounded-lg px-4 py-2 ${msg.sender === 'customer' ? 'bg-green-100 text-green-900' : msg.sender === 'agent' ? 'bg-gray-100 text-gray-900' : 'bg-gray-100 text-gray-900'}`}>
                          <p className="text-sm">{msg.text}</p>
                          <p className="text-xs mt-1 text-opacity-70">{msg.timestamp}</p>
                        </div>
                      </div>
                    ))}
                     <div ref={messagesEndRef} />
                  </div>
                </div>
                
                {ticketData.status === 'CLOSED' ? (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          This request is closed. You can no longer send messages.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-start space-x-4">
                    <div className="min-w-0 flex-1">
                      <div className="border border-gray-300 rounded-lg shadow-sm overflow-hidden">
                        <label htmlFor="message" className="sr-only">Message</label>
                        <textarea
                          rows={3}
                          name="message"
                          id="message"
                          className="block w-full py-3 px-4 resize-none border-0 focus:border-0 focus:ring-0 focus:outline-none sm:text-sm"
                          placeholder="Type your message..."
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          onFocus={(e) => e.target.classList.add('no-focus')}
                          onBlur={(e) => e.target.classList.remove('no-focus')}
                        />
                        <div className="flex justify-between items-center bg-gray-50 px-4 py-2">
                          <p className="text-xs text-gray-500">The merchant will be notified by email</p>
                          <button
                            type="button"
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            onClick={handleSendMessage}
                          >
                            <PaperPlaneIcon className="-ml-0.5 mr-2 h-4 w-4" />
                            Send
                          </button>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerTicketView;